import axios from "axios";
import { useEffect, useState } from "react";

import { useLanguage } from "../../../context/LanguageProvider";
import { getBlogPost } from "../api/blogApi";
import type { BlogPost } from "../types";

// Loads one post. `post` stays null when it doesn't exist; `error` means the request failed.
export const useBlogPost = (slug?: string) => {
    const { lang } = useLanguage();

    const [post, setPost] = useState<BlogPost | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!slug) {
            setLoading(false);
            return;
        }

        const controller = new AbortController();

        // Clear the previous post so it doesn't flash while the next one loads
        setPost(null);
        setError(false);
        setLoading(true);

        getBlogPost(slug, lang, controller.signal)
            .then(setPost)
            .catch((err) => {
                if (axios.isCancel(err)) return;
                // A 404 means "no such post", anything else is a real failure
                if (!(axios.isAxiosError(err) && err.response?.status === 404)) {
                    console.error("Error fetching blog post:", err);
                    setError(true);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [slug, lang]);

    return { post, loading, error };
};